import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { clearSalonCache } from "../lib/salon";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const DAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"],
  ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];

const CURRENCIES = [
  { code: "USD", symbol: "$",  label: "USD ($) — US Dollar" },
  { code: "EUR", symbol: "€",  label: "EUR (€) — Euro" },
  { code: "GBP", symbol: "£",  label: "GBP (£) — Pound Sterling" },
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [salon, setSalon] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSalon = async () => {
    const { data } = await api.get("/salon");
    setSalon(data);
  };
  useEffect(() => { fetchSalon(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/salon", {
        name: salon.name,
        timezone: salon.timezone || "UTC",
        slot_minutes: Number(salon.slot_minutes),
        default_country_code: salon.default_country_code,
        currency: salon.currency,
        hours: salon.hours,
        auto_complete_past_bookings: salon.auto_complete_past_bookings ?? true,
        auto_complete_grace_hours: Number(salon.auto_complete_grace_hours ?? 24),
      });
      setSalon(data);
      clearSalonCache();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!salon) return <div className="text-sm text-muted-foreground">Loading settings...</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="overline">Salon</div>
        <h1 className="font-heading text-4xl tracking-tight font-light mt-2">Settings</h1>
      </div>

      <div className="surface-card p-6 space-y-5" data-testid="salon-settings">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="overline">Salon name</Label>
            <Input
              value={salon.name}
              disabled={!isAdmin}
              onChange={(e) => setSalon({ ...salon, name: e.target.value })}
              className="bg-[#F9F8F6]"
              data-testid="settings-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="overline">Currency</Label>
            <Select
              value={salon.currency || "USD"}
              onValueChange={(v) => setSalon({ ...salon, currency: v })}
              disabled={!isAdmin}
            >
              <SelectTrigger className="bg-[#F9F8F6]" data-testid="settings-currency-select">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`settings-currency-${c.code}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Used to display prices everywhere (dashboard, bookings, services).
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2 max-w-[200px]">
            <Label className="overline">Slot granularity (min)</Label>
            <Input
              type="number"
              min="5"
              max="60"
              value={salon.slot_minutes}
              disabled={!isAdmin}
              onChange={(e) => setSalon({ ...salon, slot_minutes: e.target.value })}
              className="bg-[#F9F8F6]"
              data-testid="settings-slot-input"
            />
          </div>
          <div className="space-y-2 max-w-[260px]">
            <Label className="overline">Default country code</Label>
            <Input
              value={salon.default_country_code || ""}
              disabled={!isAdmin}
              onChange={(e) => setSalon({ ...salon, default_country_code: e.target.value })}
              placeholder="+353"
              className="bg-[#F9F8F6] font-mono"
              data-testid="settings-country-code-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Used to canonicalize local phone numbers (e.g. <span className="font-mono">0851234567 → +353851234567</span>).
            </p>
          </div>
        </div>
        <div className="border-t border-border pt-4">
          <div className="flex items-center gap-3 flex-wrap">
            <label htmlFor="salon-tz" className="text-sm font-medium min-w-[180px]">Salon timezone</label>
            <Input
              id="salon-tz"
              list="salon-tz-options"
              value={salon.timezone || ""}
              disabled={!isAdmin}
              onChange={(e) => setSalon({ ...salon, timezone: e.target.value })}
              placeholder="e.g. Europe/Dublin"
              className="bg-[#F9F8F6] max-w-[280px]"
              data-testid="salon-timezone-input"
            />
            <datalist id="salon-tz-options">
              <option value="UTC" />
              <option value="Europe/Dublin" />
              <option value="Europe/London" />
              <option value="Europe/Madrid" />
              <option value="Europe/Paris" />
              <option value="Europe/Berlin" />
              <option value="America/New_York" />
              <option value="America/Chicago" />
              <option value="America/Denver" />
              <option value="America/Los_Angeles" />
              <option value="Asia/Tokyo" />
              <option value="Australia/Sydney" />
            </datalist>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2" data-testid="settings-tz-note">
            Determines what "now" means for past-time checks (e.g. "earliest" availability).
            Standard IANA name (defaults to UTC if blank). Calendar, Sophie and bookings
            already render in the same salon-local wall-clock you see here.
          </p>
        </div>
      </div>

      <div className="surface-card p-6" data-testid="salon-hours">
        <h2 className="font-heading text-2xl tracking-tight mb-4">Opening hours</h2>
        <div className="space-y-3">
          {DAYS.map(([k, label]) => {
            const day = salon.hours[k];
            return (
              <div key={k} className="flex items-center gap-3">
                <div className="w-28 text-sm font-medium">{label}</div>
                <Switch
                  checked={day.open}
                  disabled={!isAdmin}
                  onCheckedChange={(v) => setSalon({ ...salon, hours: { ...salon.hours, [k]: { ...day, open: v } } })}
                  data-testid={`hours-${k}-switch`}
                />
                <Input type="time" value={day.start} disabled={!day.open || !isAdmin} onChange={(e) => setSalon({ ...salon, hours: { ...salon.hours, [k]: { ...day, start: e.target.value } } })} className="bg-[#F9F8F6] flex-1 max-w-[160px]" />
                <span className="text-muted-foreground">→</span>
                <Input type="time" value={day.end} disabled={!day.open || !isAdmin} onChange={(e) => setSalon({ ...salon, hours: { ...salon.hours, [k]: { ...day, end: e.target.value } } })} className="bg-[#F9F8F6] flex-1 max-w-[160px]" />
              </div>
            );
          })}
        </div>
      </div>

      <div className="surface-card p-6" data-testid="auto-complete-panel">
        <h2 className="font-heading text-2xl tracking-tight mb-1">Booking lifecycle</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Past bookings that staff never manually mark as completed will be
          auto-completed after a grace period. Cancelled and already-completed
          bookings are never touched.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <Switch
            checked={salon.auto_complete_past_bookings ?? true}
            disabled={!isAdmin}
            onCheckedChange={(v) => setSalon({ ...salon, auto_complete_past_bookings: v })}
            data-testid="auto-complete-switch"
          />
          <span className="text-sm font-medium">Auto-complete past bookings</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap mt-4">
          <label htmlFor="grace-hours" className="text-sm font-medium min-w-[180px]">
            Grace period (hours)
          </label>
          <Input
            id="grace-hours"
            type="number"
            min={0}
            max={720}
            value={salon.auto_complete_grace_hours ?? 24}
            disabled={!isAdmin || !(salon.auto_complete_past_bookings ?? true)}
            onChange={(e) => setSalon({ ...salon, auto_complete_grace_hours: e.target.value === "" ? 0 : Number(e.target.value) })}
            className="bg-[#F9F8F6] max-w-[120px]"
            data-testid="grace-hours-input"
          />
          <span className="text-xs text-muted-foreground">default 24 — set to 0 to complete immediately after end time</span>
        </div>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-[#2B4F39]" data-testid="settings-save-btn">
            {saving ? "Saving..." : "Save settings"}
          </Button>
        </div>
      )}

      {/* Public Booking */}
      <PublicBookingPanel salon={salon} setSalon={setSalon} isAdmin={isAdmin} />

      {/* Website Widget */}
      <WebsiteWidgetPanel salon={salon} setSalon={setSalon} isAdmin={isAdmin} />

      {/* WhatsApp Booking (Phase 10A) */}
      <WhatsAppPanel salon={salon} setSalon={setSalon} isAdmin={isAdmin} />

      {/* System Status (Phase 10A) */}
      {isAdmin && <SystemStatusPanel />}
    </div>
  );
}


function PublicBookingPanel({ salon, setSalon, isAdmin }) {
  const [toggling, setToggling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotating, setRotating] = useState(false);

  const publicUrl = salon?.public_booking_token
    ? `${window.location.origin}/book/${salon.public_booking_token}`
    : null;

  const toggleEnabled = async (enabled) => {
    setToggling(true);
    try {
      const { data } = await api.put("/salon", { public_booking_enabled: enabled });
      setSalon(data);
      clearSalonCache();
      toast.success(enabled ? "Public booking enabled" : "Public booking disabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setToggling(false);
    }
  };

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied to clipboard");
    });
  };

  const doRotate = async () => {
    setRotating(true);
    try {
      const { data } = await api.post("/salon/regenerate-public-token");
      setSalon(data);
      clearSalonCache();
      toast.success("Public link regenerated — old link is now invalid");
      setConfirmRotate(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRotating(false);
    }
  };

  if (!salon) return null;

  return (
    <div className="surface-card p-6 space-y-4" data-testid="public-booking-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl tracking-tight">Public booking link</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Share this link so customers can book appointments directly — no account required.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-shrink-0 pt-1">
            <span className="text-xs text-muted-foreground">
              {salon.public_booking_enabled ? "On" : "Off"}
            </span>
            <Switch
              checked={!!salon.public_booking_enabled}
              disabled={toggling}
              onCheckedChange={toggleEnabled}
              data-testid="public-booking-toggle"
            />
          </div>
        )}
      </div>

      {salon.public_booking_enabled ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div
              className="flex-1 bg-[#F9F8F6] border border-[#E0DBD0] rounded-md px-3 py-2 text-sm font-mono text-[#1C201E] truncate"
              data-testid="public-booking-url"
            >
              {publicUrl}
            </div>
            <Button
              onClick={copyLink}
              size="sm"
              variant="outline"
              className="flex-shrink-0"
              data-testid="copy-public-link-btn"
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              Share via Instagram bio, WhatsApp, SMS, or your website. All scheduling rules are enforced automatically.
            </p>
            {isAdmin && (
              <button
                onClick={() => setConfirmRotate(true)}
                className="text-[11px] text-muted-foreground hover:text-[#A85D5D] underline-offset-2 hover:underline flex-shrink-0 ml-3"
                data-testid="regenerate-public-token-btn"
              >
                Regenerate link
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-[#F9F8F6] border border-[#E0DBD0] rounded-md px-4 py-3">
          <p className="text-sm text-muted-foreground">
            Enable public booking to get a shareable link for your customers.
          </p>
          {publicUrl && (
            <p className="text-xs text-muted-foreground mt-1 font-mono truncate opacity-50">
              {publicUrl}
            </p>
          )}
        </div>
      )}

      <AlertDialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <AlertDialogContent data-testid="regenerate-token-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate public booking link?</AlertDialogTitle>
            <AlertDialogDescription>
              This invalidates the current link immediately — anyone using the old URL
              (Instagram bio, embedded snippet, saved bookmark) will see a 404. All
              bookings, customers, and history are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="regenerate-token-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRotate}
              disabled={rotating}
              data-testid="regenerate-token-confirm"
              className="bg-[#A85D5D] hover:bg-[#8E4A4A]"
            >
              {rotating ? "Regenerating…" : "Yes, regenerate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isAdmin && (
        <ExpectationMessageEditor salon={salon} setSalon={setSalon} />
      )}
    </div>
  );
}


function ExpectationMessageEditor({ salon, setSalon }) {
  const MAX = 300;
  const [draft, setDraft] = useState(salon?.customer_expectation_message || "");
  const [saving, setSaving] = useState(false);
  // Keep local draft in sync when an external refresh changes the salon doc
  useEffect(() => {
    setDraft(salon?.customer_expectation_message || "");
  }, [salon?.customer_expectation_message]);

  const trimmed = draft.trim();
  const original = (salon?.customer_expectation_message || "").trim();
  const dirty = trimmed !== original;
  const remaining = MAX - draft.length;

  const save = async () => {
    if (draft.length > MAX) {
      toast.error(`Message must be ${MAX} characters or fewer.`);
      return;
    }
    setSaving(true);
    try {
      // Send the trimmed value so the persisted form is clean. An empty
      // string clears the field (frontend hides the section when empty).
      const { data } = await api.put("/salon", { customer_expectation_message: trimmed });
      setSalon(data);
      clearSalonCache();
      toast.success(trimmed ? "What-to-expect message saved" : "What-to-expect message cleared");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-[#E0DBD0] pt-4 space-y-2" data-testid="expectation-editor">
      <div>
        <div className="text-sm font-medium text-[#1C201E]">What customers should expect</div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Optional short note shown on the booking review step. Use it to set expectations or add a warm welcome.
        </p>
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, MAX))}
        placeholder="e.g. Plan for around 2 hours. We'll have refreshments ready while your colour develops."
        maxLength={MAX}
        rows={3}
        className="w-full bg-white border border-[#E0DBD0] focus:border-[#1A3626] rounded-md px-3 py-2 text-sm resize-y"
        data-testid="expectation-textarea"
      />
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span data-testid="expectation-char-count">{remaining} characters remaining</span>
        <Button
          onClick={save}
          size="sm"
          variant="outline"
          disabled={!dirty || saving || draft.length > MAX}
          data-testid="expectation-save-btn"
        >
          {saving ? "Saving…" : "Save message"}
        </Button>
      </div>
    </div>
  );
}


function WebsiteWidgetPanel({ salon, setSalon, isAdmin }) {
  const [toggling, setToggling] = useState(false);
  const [copied, setCopied] = useState(false);

  const widgetUrl = salon?.public_booking_token
    ? `${window.location.origin}/widget/${salon.public_booking_token}`
    : null;

  // Inline embed snippet — creates a floating button + iframe on any site
  const embedSnippet = widgetUrl
    ? `<!-- Sophie Booking Widget -->
<script>
(function(){
  var ORIGIN="${window.location.origin}";
  var TOKEN="${salon.public_booking_token}";
  var btn=document.createElement("button");
  btn.setAttribute("id","sophie-widget-btn");
  btn.style.cssText="position:fixed;bottom:24px;right:24px;width:56px;height:56px;border-radius:50%;background:#1A3626;border:none;cursor:pointer;z-index:2147483647;box-shadow:0 4px 16px rgba(0,0,0,.2);display:flex;align-items:center;justify-content:center";
  btn.innerHTML='<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" fill="white"/></svg>';
  document.body.appendChild(btn);
  var frame=document.createElement("iframe");
  frame.src=ORIGIN+"/widget/"+TOKEN;
  frame.allow="clipboard-write";
  frame.style.cssText="display:none;position:fixed;bottom:96px;right:24px;width:400px;height:640px;border:none;border-radius:20px;box-shadow:0 8px 40px rgba(0,0,0,.15);z-index:2147483646;max-width:calc(100vw - 48px);max-height:calc(100vh - 120px)";
  document.body.appendChild(frame);
  var open=false;
  btn.addEventListener("click",function(){
    open=!open;
    frame.style.display=open?"block":"none";
    btn.innerHTML=open?'<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="white" stroke-width="2.5" stroke-linecap="round"/></svg>':'<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" fill="white"/></svg>';
  });
})();
</script>`
    : "";

  const toggleEnabled = async (enabled) => {
    setToggling(true);
    try {
      const { data } = await api.put("/salon", { widget_enabled: enabled });
      setSalon(data);
      clearSalonCache();
      toast.success(enabled ? "Website widget enabled" : "Website widget disabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setToggling(false);
    }
  };

  const copySnippet = () => {
    navigator.clipboard.writeText(embedSnippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Embed snippet copied");
    });
  };

  if (!salon) return null;

  return (
    <div className="surface-card p-6 space-y-4" data-testid="website-widget-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl tracking-tight">Website widget</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Embed a Sophie chat bubble on any website. Customers book conversationally — no forms.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-shrink-0 pt-1">
            <span className="text-xs text-muted-foreground">
              {salon.widget_enabled ? "On" : "Off"}
            </span>
            <Switch
              checked={!!salon.widget_enabled}
              disabled={toggling}
              onCheckedChange={toggleEnabled}
              data-testid="widget-enabled-toggle"
            />
          </div>
        )}
      </div>

      {salon.widget_enabled ? (
        <div className="space-y-3">
          {isAdmin && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="overline">Greeting (optional)</Label>
                <Input
                  value={salon.widget_greeting || ""}
                  onChange={(e) => setSalon({ ...salon, widget_greeting: e.target.value })}
                  placeholder="Hi! How can I help you book today?"
                  className="bg-[#F9F8F6]"
                  data-testid="widget-greeting-input"
                  maxLength={140}
                  onBlur={async () => {
                    try {
                      const { data } = await api.put("/salon", { widget_greeting: salon.widget_greeting || "" });
                      setSalon(data);
                      clearSalonCache();
                    } catch (e) {
                      toast.error(formatApiError(e.response?.data?.detail) || e.message);
                    }
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Accent color</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    value={salon.widget_accent_color || "#1A3626"}
                    onChange={(e) => setSalon({ ...salon, widget_accent_color: e.target.value })}
                    className="bg-[#F9F8F6] h-10 w-16 p-1 cursor-pointer"
                    data-testid="widget-accent-input"
                    onBlur={async () => {
                      try {
                        const { data } = await api.put("/salon", { widget_accent_color: salon.widget_accent_color });
                        setSalon(data);
                        clearSalonCache();
                      } catch (e) {
                        toast.error(formatApiError(e.response?.data?.detail) || e.message);
                      }
                    }}
                  />
                  <span className="text-xs font-mono text-muted-foreground">{salon.widget_accent_color || "#1A3626"}</span>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div
              className="flex-1 bg-[#F9F8F6] border border-[#E0DBD0] rounded-md px-3 py-2 text-sm font-mono text-[#1C201E] truncate"
              data-testid="widget-preview-url"
            >
              {widgetUrl}
            </div>
            <a
              href={widgetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 text-xs text-[#1A3626] underline-offset-2 hover:underline"
              data-testid="widget-preview-link"
            >
              Preview
            </a>
          </div>

          <div className="relative">
            <pre className="bg-[#1C201E] text-[#9DB5A6] text-[11px] rounded-lg p-4 overflow-x-auto whitespace-pre-wrap leading-relaxed" data-testid="widget-embed-snippet">
              {embedSnippet}
            </pre>
            <Button
              onClick={copySnippet}
              size="sm"
              variant="outline"
              className="absolute top-3 right-3 bg-[#1C201E]/80 border-[#5C635A] text-white hover:bg-[#1C201E] text-xs"
              data-testid="copy-embed-btn"
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Paste this snippet before the closing <code className="text-[10px] bg-[#F0EDE7] px-1 rounded">&lt;/body&gt;</code> tag on your website. Works on any site — WordPress, Squarespace, Wix, Webflow, or custom HTML.
          </p>
        </div>
      ) : (
        <div className="bg-[#F9F8F6] border border-[#E0DBD0] rounded-md px-4 py-3">
          <p className="text-sm text-muted-foreground">
            Enable the widget to get an embed snippet for your website.
          </p>
        </div>
      )}
    </div>
  );
}


function WhatsAppPanel({ salon, setSalon, isAdmin }) {
  const [toggling, setToggling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const toggleEnabled = async (enabled) => {
    setToggling(true);
    try {
      const { data } = await api.put("/salon", { whatsapp_enabled: enabled });
      setSalon(data);
      clearSalonCache();
      toast.success(enabled ? "WhatsApp booking enabled" : "WhatsApp booking disabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setToggling(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.get("/integrations/whatsapp/test");
      setTestResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setTesting(false);
    }
  };

  if (!salon) return null;

  return (
    <div className="surface-card p-6 space-y-4" data-testid="whatsapp-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl tracking-tight">WhatsApp booking
            <span className="ml-2 text-[10px] tracking-widest uppercase bg-[#F2EFE8] text-[#5C635A] px-2 py-0.5 rounded-full align-middle">Beta</span>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Customers message your WhatsApp number and Sophie books them in. Inbound is free; outbound confirmations require Twilio.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-shrink-0 pt-1">
            <span className="text-xs text-muted-foreground">
              {salon.whatsapp_enabled ? "On" : "Off"}
            </span>
            <Switch
              checked={!!salon.whatsapp_enabled}
              disabled={toggling}
              onCheckedChange={toggleEnabled}
              data-testid="whatsapp-enabled-toggle"
            />
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="flex items-center justify-between border-t border-border pt-4">
          <p className="text-[11px] text-muted-foreground">
            Validate your Twilio configuration — runs locally, no real messages sent.
          </p>
          <Button
            onClick={runTest}
            disabled={testing}
            size="sm"
            variant="outline"
            data-testid="whatsapp-test-btn"
          >
            {testing ? "Testing…" : "Test configuration"}
          </Button>
        </div>
      )}

      {testResult && (
        <div className="space-y-2" data-testid="whatsapp-test-result">
          <div className={`text-xs font-medium ${
            testResult.summary === "ok" ? "text-[#1A3626]" :
            testResult.summary === "warning" ? "text-[#A8843C]" : "text-[#A85D5D]"
          }`}>
            Status: {testResult.summary.toUpperCase()}
          </div>
          <div className="space-y-1.5">
            {testResult.checks.map((c, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-xs"
                data-testid={`whatsapp-test-check-${c.severity}`}
              >
                <span className={`mt-0.5 inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                  c.severity === "ok" ? "bg-[#5C8262]" :
                  c.severity === "warning" ? "bg-[#D4A847]" : "bg-[#A85D5D]"
                }`} />
                <div className="flex-1">
                  <span className="font-medium">{c.label}</span>
                  {c.detail && <span className="text-muted-foreground"> — {c.detail}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function SystemStatusPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/system/status");
      setStatus(data);
    } catch (e) {
      // non-fatal — admin may still toggle channels via individual panels
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Auto-refresh every 30s
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, []);

  if (loading && !status) {
    return (
      <div className="surface-card p-6 text-sm text-muted-foreground" data-testid="system-status-loading">
        Loading system status…
      </div>
    );
  }
  if (!status) return null;

  const Dot = ({ on }) => (
    <span className={`inline-block w-2 h-2 rounded-full ${on ? "bg-[#5C8262]" : "bg-[#C7B8A0]"}`} />
  );

  return (
    <div className="surface-card p-6 space-y-4" data-testid="system-status-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl tracking-tight">System status</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Real-time view of channels, worker, and queue. Auto-refreshes every 30s.
          </p>
        </div>
        <button
          onClick={refresh}
          className="text-[11px] text-muted-foreground hover:text-[#1A3626] underline-offset-2 hover:underline"
          data-testid="system-status-refresh"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div className="space-y-2">
          <div className="overline text-[10px] text-muted-foreground">Channels</div>
          <div className="flex items-center justify-between" data-testid="status-widget">
            <span>Website widget</span>
            <span className="flex items-center gap-1.5 text-xs">
              <Dot on={status.channels.widget_enabled} />
              {status.channels.widget_enabled ? "Enabled" : "Off"}
            </span>
          </div>
          <div className="flex items-center justify-between" data-testid="status-public-booking">
            <span>Public booking</span>
            <span className="flex items-center gap-1.5 text-xs">
              <Dot on={status.channels.public_booking_enabled} />
              {status.channels.public_booking_enabled ? "Enabled" : "Off"}
            </span>
          </div>
          <div className="flex items-center justify-between" data-testid="status-whatsapp">
            <span>WhatsApp</span>
            <span className="flex items-center gap-1.5 text-xs">
              <Dot on={status.channels.whatsapp_enabled} />
              {status.channels.whatsapp_enabled ? "Enabled" : "Off"}
            </span>
          </div>
        </div>
        <div className="space-y-2">
          <div className="overline text-[10px] text-muted-foreground">WhatsApp delivery</div>
          <div className="flex items-center justify-between" data-testid="status-worker">
            <span>Outbound worker</span>
            <span className="flex items-center gap-1.5 text-xs">
              <Dot on={status.whatsapp.worker_running} />
              {status.whatsapp.worker_running ? "Running" : "Stopped"}
            </span>
          </div>
          <div className="flex items-center justify-between" data-testid="status-outbound">
            <span>Outbound sending</span>
            <span className="flex items-center gap-1.5 text-xs">
              <Dot on={status.whatsapp.outbound_enabled} />
              {status.whatsapp.outbound_enabled ? "Enabled" : "Off"}
            </span>
          </div>
          <div className="flex items-center justify-between" data-testid="status-queue">
            <span>Queue backlog</span>
            <span className="text-xs font-mono">
              {status.queue.queued + status.queue.retry_backlog}
              {status.queue.failed + status.queue.abandoned > 0 && (
                <span className="text-[#A85D5D] ml-2">
                  ({status.queue.failed + status.queue.abandoned} failed)
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center justify-between" data-testid="status-sent-24h">
            <span>Sent last 24h</span>
            <span className="text-xs font-mono text-muted-foreground">{status.queue.sent_last_24h}</span>
          </div>
        </div>
      </div>

      {status.warnings && status.warnings.length > 0 && (
        <div className="border-t border-border pt-4 space-y-1.5" data-testid="status-warnings">
          <div className="overline text-[10px] text-muted-foreground">Warnings</div>
          {status.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-[#A8843C]">
              <span className="mt-0.5 inline-block w-1.5 h-1.5 rounded-full bg-[#D4A847] flex-shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

